<template>
  <div>
    <div v-if="front.authDialogOpen" class="auth-dialog-backdrop" role="dialog" aria-modal="true" aria-label="登录注册">
      <article class="auth-dialog">
        <div class="auth-dialog-head">
          <h3>{{ authTitle }}</h3>
          <button type="button" class="text-link" @click="front.authDialogOpen = false">关闭</button>
        </div>
        <div v-if="front.authMode !== 'forgot'" class="auth-tabs">
          <button type="button" :class="{ selected: front.authMode === 'login' }" @click="front.authMode = 'login'">登录</button>
          <button type="button" :class="{ selected: front.authMode === 'register' }" @click="front.authMode = 'register'">注册</button>
        </div>

        <div class="auth-dialog-body">
          <template v-if="front.authMode === 'login'">
            <section class="auth-method-section" aria-labelledby="front-login-method-title">
              <div class="auth-method-heading">
                <div>
                  <span class="auth-method-kicker">SIGN IN METHOD</span>
                  <h4 id="front-login-method-title">选择登录方式</h4>
                </div>
                <span class="auth-method-safe">安全认证</span>
              </div>
              <div class="auth-method-grid">
                <div class="auth-method-card selected" aria-current="true">
                  <span class="auth-method-icon auth-method-icon--account" aria-hidden="true">密</span>
                  <span class="auth-method-copy">
                    <strong>账号密码</strong>
                    <small>使用注册邮箱登录</small>
                  </span>
                  <span class="auth-method-check" aria-hidden="true">✓</span>
                </div>
                <button
                  type="button"
                  class="auth-method-card auth-method-card--cloud"
                  :disabled="front.cstCloudStarting"
                  @click="front.startCstCloudLogin"
                >
                  <span class="auth-method-icon auth-method-icon--cloud" aria-hidden="true">CST</span>
                  <span class="auth-method-copy">
                    <strong>{{ front.cstCloudStarting ? '正在跳转...' : '中国科技云' }}</strong>
                    <small>统一身份认证登录</small>
                  </span>
                  <span class="auth-method-arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </section>
            <div class="auth-password-heading">
              <strong>账号密码登录</strong>
              <span>请输入本站注册信息</span>
            </div>
            <label>
              电子邮箱
              <input v-model="front.authForm.account" type="email" autocomplete="username" />
            </label>
            <label>
              登录密码
              <input v-model="front.authForm.password" type="password" autocomplete="current-password" />
            </label>
            <button type="button" class="text-link inline-help" @click="front.openForgotPassword">忘记密码？</button>
          </template>

          <template v-else-if="front.authMode === 'register'">
            <div class="auth-register-form">
              <div class="auth-register-row">
                <label>
                  <span class="auth-field-label">真实姓名 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                  <input v-model="front.authForm.name" type="text" maxlength="50" />
                </label>
                <fieldset class="auth-choice-field">
                  <legend>性别 <span class="auth-required-mark" aria-hidden="true">*</span></legend>
                  <div class="auth-option-grid gender-option-grid">
                    <label class="auth-option" :class="{ selected: front.authForm.gender === '男' }">
                      <input v-model="front.authForm.gender" type="radio" value="男" name="register-gender" />
                      <span>男</span>
                    </label>
                    <label class="auth-option" :class="{ selected: front.authForm.gender === '女' }">
                      <input v-model="front.authForm.gender" type="radio" value="女" name="register-gender" />
                      <span>女</span>
                    </label>
                  </div>
                </fieldset>
              </div>

              <label class="auth-register-span">
                <span class="auth-field-label">
                  电子邮箱 <span class="auth-required-mark" aria-hidden="true">*</span>
                  <span class="auth-label-note">注册后不可修改</span>
                </span>
                <input v-model="front.authForm.account" type="email" autocomplete="username" />
              </label>

              <div class="auth-field auth-register-span">
                <span class="auth-field-label">邮箱验证码 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                <span class="captcha-row">
                  <input
                    id="register-email-otp"
                    v-model="front.authForm.emailOtp"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="6位验证码"
                  />
                  <button
                    type="button"
                    class="ghost-action compact captcha-action"
                    :disabled="front.emailCodeSeconds > 0 || front.emailCodeSending"
                    :aria-busy="front.emailCodeSending"
                    @click.stop.prevent="sendRegisterEmailCode"
                  >
                    {{ registerCodeButtonLabel }}
                  </button>
                </span>
              </div>
              <div class="auth-register-row">
                <label>
                  <span class="auth-field-label">登录密码 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                  <input v-model="front.authForm.password" type="password" autocomplete="new-password" />
                  <small class="auth-label-note">{{ passwordHint }}</small>
                </label>
                <label>
                  <span class="auth-field-label">确认密码 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                  <input v-model="front.authForm.confirmPassword" type="password" autocomplete="new-password" />
                </label>
              </div>

              <div class="auth-register-row">
                <label>
                  <span class="auth-field-label">工作/学习单位 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                  <input v-model="front.authForm.unit" type="text" maxlength="200" />
                </label>
                <label>
                  <span class="auth-field-label">学校/工作单位税号 <span class="auth-required-mark" aria-hidden="true">*</span></span>
                  <input v-model="front.authForm.orgCreditCode" type="text" maxlength="32" />
                </label>
              </div>

              <label class="auth-register-span">
                <span class="auth-field-label">职称</span>
                <input v-model="front.authForm.title" type="text" maxlength="50" />
              </label>

              <fieldset class="auth-choice-field auth-register-span">
                <legend>职务类型 <span class="auth-required-mark" aria-hidden="true">*</span></legend>
                <div class="auth-option-grid role-option-grid">
                  <label class="auth-option" :class="{ selected: front.authForm.role === '教师' }">
                    <input v-model="front.authForm.role" type="radio" value="教师" name="register-role" />
                    <span>教师 / 科研</span>
                  </label>
                  <label class="auth-option" :class="{ selected: front.authForm.role === '学生' }">
                    <input v-model="front.authForm.role" type="radio" value="学生" name="register-role" />
                    <span>学生</span>
                  </label>
                  <label class="auth-option" :class="{ selected: front.authForm.role === '嘉宾' }">
                    <input v-model="front.authForm.role" type="radio" value="嘉宾" name="register-role" />
                    <span>嘉宾 / 其他</span>
                  </label>
                </div>
              </fieldset>

              <p class="service-hint auth-register-hint auth-register-span">{{ front.registerEmailOtpHint }}</p>
            </div>
          </template>

          <template v-else>
            <p class="service-hint">请输入注册邮箱并完成验证码校验后重置密码。</p>
            <label>
              注册电子邮箱
              <input v-model="front.forgotPasswordEmail" type="email" />
            </label>
            <div class="auth-field">
              <span class="auth-field-label">邮箱验证码</span>
              <span class="captcha-row">
                <input
                  id="forgot-email-otp"
                  v-model="front.forgotPasswordCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="6位验证码"
                />
                <button
                  type="button"
                  class="ghost-action compact captcha-action"
                  :disabled="front.forgotCodeSeconds > 0 || front.forgotCodeSending"
                  :aria-busy="front.forgotCodeSending"
                  @click.stop.prevent="sendForgotPasswordCode"
                >
                  {{ forgotCodeButtonLabel }}
                </button>
              </span>
            </div>
            <label>
              新密码
              <input v-model="front.forgotPasswordNew" type="password" autocomplete="new-password" />
              <small class="auth-label-note">{{ passwordHint }}</small>
            </label>
            <label>
              确认新密码
              <input v-model="front.forgotPasswordConfirm" type="password" autocomplete="new-password" />
            </label>
            <button type="button" class="text-link inline-help" @click="front.authMode = 'login'">返回登录</button>
          </template>

          <slide-captcha
            v-if="front.authMode === 'login' || front.authMode === 'register'"
            :key="front.authMode"
            ref="slideCaptcha"
            :request="slideRequest"
            @ticket="front.authForm.slideTicket = $event"
          />
        </div>

        <div class="auth-dialog-foot">
          <button type="button" class="primary-action" :disabled="front.authSubmitting" @click="front.submitAuth">
            {{ submitLabel }}
          </button>
          <p v-if="front.authMode === 'login'" class="auth-agreement-hint">
            继续登录即表示您已阅读并同意本站用户协议和隐私政策。
          </p>
        </div>
      </article>
    </div>
    <div
      v-if="front.cstCloudUnavailableOpen"
      class="cstcloud-unavailable-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cstcloud-unavailable-title"
      @click.self="front.closeCstCloudUnavailable"
    >
      <article class="cstcloud-unavailable-dialog">
        <div class="cstcloud-unavailable-icon" aria-hidden="true">!</div>
        <h3 id="cstcloud-unavailable-title">中国科技云登录暂未开放</h3>
        <p>{{ front.cstCloudUnavailableMessage }}</p>
        <button type="button" class="primary-action" autofocus @click="front.closeCstCloudUnavailable">我知道了</button>
      </article>
    </div>
    <div
      v-if="front.authFeedbackDialogOpen && front.authFeedback"
      class="auth-feedback-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="auth-feedback-title"
      @click.self="front.closeAuthFeedback"
    >
      <article class="auth-feedback-dialog" :class="front.authFeedbackClass">
        <div class="auth-feedback-dialog__icon" aria-hidden="true">{{ feedbackIcon }}</div>
        <h3 id="auth-feedback-title">{{ feedbackTitle }}</h3>
        <p>{{ front.authFeedback }}</p>
        <button type="button" class="primary-action" autofocus @click="front.closeAuthFeedback">我知道了</button>
      </article>
    </div>
  </div>
</template>

<script>
import SlideCaptcha from './SlideCaptcha.vue'
import { issueSlideCaptcha, verifySlideCaptcha } from '../../api/paleo'
import { passwordPolicyHint } from '../../utils/passwordPolicy'

export default {
  name: 'AuthModals',
  components: { SlideCaptcha },
  inject: ['front'],
  computed: {
    passwordHint() {
      return passwordPolicyHint()
    },
    authTitle() {
      if (this.front.authMode === 'register') return '注册账号'
      if (this.front.authMode === 'forgot') return '重置密码'
      return '欢迎登录'
    },
    submitLabel() {
      if (this.front.authSubmitting) return '提交中...'
      if (this.front.authMode === 'register') return '提交注册'
      if (this.front.authMode === 'forgot') return '确认重置'
      return '立即登录'
    },
    registerCodeButtonLabel() {
      if (this.front.emailCodeSending) return '发送中...'
      if (this.front.emailCodeSeconds > 0) return `${this.front.emailCodeSeconds}s`
      return '获取验证码'
    },
    forgotCodeButtonLabel() {
      if (this.front.forgotCodeSending) return '发送中...'
      if (this.front.forgotCodeSeconds > 0) return `${this.front.forgotCodeSeconds}s`
      return '获取验证码'
    },
    feedbackTitle() {
      return this.front.authFeedbackIsError ? '请检查填写内容' : '操作成功'
    },
    feedbackIcon() {
      return this.front.authFeedbackIsError ? '!' : '✓'
    }
  },
  methods: {
    async slideRequest(options) {
      if (options.method === 'GET') {
        return issueSlideCaptcha()
      }
      return verifySlideCaptcha(options.body)
    },
    sendRegisterEmailCode() {
      this.front.sendRegisterEmailCode()
    },
    sendForgotPasswordCode() {
      this.front.sendForgotPasswordCode()
    },
    refreshSlideCaptcha() {
      if (this.$refs.slideCaptcha) {
        this.$refs.slideCaptcha.refresh()
      }
    }
  }
}
</script>
